import { useState, useEffect } from "react";
import { Download, X, Share, Plus, MoreVertical } from "lucide-react";

const isStandalone = () =>
  typeof window !== "undefined" &&
  (window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true);

const isIOS = () =>
  typeof window !== "undefined" &&
  /iphone|ipad|ipod/i.test(window.navigator.userAgent) &&
  !window.MSStream;

const Step = ({ n, children }) => (
  <li className="flex items-center gap-2">
    <span className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold shrink-0">{n}</span>
    {children}
  </li>
);

export default function InstallApp() {
  const [deferred, setDeferred] = useState(null);
  const [modal, setModal] = useState(null); // "ios" | "generic" | null
  const [hidden, setHidden] = useState(isStandalone());

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => setHidden(true);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (hidden) return null;

  const onClick = async () => {
    if (deferred) {
      deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice?.outcome === "accepted") setHidden(true);
      setDeferred(null);
      return;
    }
    setModal(isIOS() ? "ios" : "generic");
  };

  return (
    <>
      <button
        onClick={onClick}
        data-testid="install-app-button"
        aria-label="Installer l'application Le Véridique"
        className="inline-flex items-center gap-1.5 bg-[#E11D48] text-white px-3 py-1 rounded-full font-sub text-xs font-semibold hover:bg-[#be123c] transition-colors shadow-sm whitespace-nowrap"
      >
        <Download size={13} /> Installer l'app
      </button>

      {modal && (
        <div
          className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          data-testid="install-modal"
          onClick={() => setModal(null)}
        >
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-headline text-xl font-black text-[#0A0A0A]">Installer Le Véridique</h3>
              <button onClick={() => setModal(null)} data-testid="install-modal-close" className="text-slate-400 hover:text-slate-600">
                <X size={22} />
              </button>
            </div>
            <p className="font-body text-sm text-slate-500 mb-4">Ajoutez l'application à votre écran d'accueil pour y accéder en un seul geste, comme une vraie appli.</p>
            {modal === "ios" ? (
              <ol className="font-body text-sm text-slate-700 space-y-3">
                <Step n="1">Appuyez sur <Share size={16} className="inline text-[#E11D48]" /> <strong>Partager</strong> dans Safari.</Step>
                <Step n="2">Choisissez <Plus size={16} className="inline text-[#E11D48]" /> <strong>Sur l'écran d'accueil</strong>.</Step>
                <Step n="3">Appuyez sur <strong>Ajouter</strong>. C'est fait !</Step>
              </ol>
            ) : (
              <ol className="font-body text-sm text-slate-700 space-y-3">
                <Step n="1">Ouvrez le menu <MoreVertical size={16} className="inline text-[#E11D48]" /> de votre navigateur (en haut à droite).</Step>
                <Step n="2">Choisissez <strong>« Installer l'application »</strong> ou <strong>« Ajouter à l'écran d'accueil »</strong>.</Step>
                <Step n="3">Confirmez. L'icône Le Véridique apparaîtra sur votre appareil.</Step>
              </ol>
            )}
          </div>
        </div>
      )}
    </>
  );
}
